/** What the panel has always been, and what a reset goes back to. */
export const COPILOT_PANEL_DEFAULT_WIDTH = 360

/**
 * Narrowest the panel may be dragged.
 *
 * A suggestion card carries a quote, a speaker and a route chip on one row;
 * below this they stop fitting and the card turns into a column of fragments.
 */
export const COPILOT_PANEL_MIN_WIDTH = 280

/** Widest, before the transcript it sits beside starts losing more than it gains. */
export const COPILOT_PANEL_MAX_WIDTH = 640

/**
 * How long the panel takes to slide in or out.
 *
 * Shared by the CSS that runs the slide and the hook that keeps the panel in
 * the tree while it does: a mismatch either strands the panel after the
 * animation or unmounts it mid-slide.
 */
export const COPILOT_PANEL_TRANSITION_MS = 200

/** How far one arrow key moves the edge. */
export const COPILOT_PANEL_WIDTH_STEP = 16

/**
 * The width to actually use for the Copilot panel (BRAI-681).
 *
 * Also bounded by the window, not only by the constants: the panel is only
 * draggable from `lg` up, which starts at 1024px, and 640px of that would
 * leave the transcript the smaller half of its own screen. Half the viewport
 * is the ceiling whenever that is the tighter of the two.
 *
 * @param width Requested width, from a drag, an arrow key, or storage written
 *   by an older build.
 * @param viewportWidth Window width to stay inside.
 * @returns A width within both bounds, rounded to whole pixels.
 */
export function clampCopilotPanelWidth(width: number, viewportWidth: number): number {
  const ceiling = Math.min(COPILOT_PANEL_MAX_WIDTH, Math.floor(viewportWidth / 2))

  // A window too narrow to honour both: the minimum wins. Below it the panel
  // cannot show a suggestion at all, and a panel that shows nothing is worse
  // than one that crowds the transcript.
  if (ceiling < COPILOT_PANEL_MIN_WIDTH) return COPILOT_PANEL_MIN_WIDTH

  if (!Number.isFinite(width)) return COPILOT_PANEL_DEFAULT_WIDTH

  return Math.min(Math.max(Math.round(width), COPILOT_PANEL_MIN_WIDTH), ceiling)
}
